/**
 * ownerParamRoutes.jsx — Wrappers des routes PARAMÉTRÉES du shell owner (Sprint
 * routeur, Lot 2).
 *
 * Chaque wrapper : lit l':id / les query params (useParams/useSearchParams),
 * RECHARGE l'objet via le chargeur EXISTANT (openHorsePage / loadSessions) quand
 * l'état n'est pas déjà posé (deep-link, refresh, back/forward), puis rend le
 * composant enfant — qui reste AGNOSTIQUE du routeur (il garde ses props
 * onBack/onNavigate). C'est ICI (couche routing) qu'on mappe vers navigate.
 *
 * Boutons Retour → navigate(parentPath) FIXE, JAMAIS navigate(-1) (un deep-link
 * sortirait du site). Le reload-by-id réutilise les fetch existants : aucun
 * doublon de requête. /sessions/:id n'est PAS ici (branche dédiée d'App.jsx,
 * layout sans bannières).
 */
import React, { lazy, useEffect, useMemo, useRef } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PAGES } from '../constants/pages';
import { parseCompareIds } from '../sessions/compareIds';
import CompareTruncatedNotice from '../sessions/CompareTruncatedNotice';
import useRangeSearchParams from '../sessions/useRangeSearchParams';
import { useSessionsListVisit } from '../sessions/useSessionsListVisit';

const HorseDetail          = lazy(() => import('../horses/HorseDetail'));
const HealthJournal        = lazy(() => import('../horses/HealthJournal'));
const EkynAirLiveDashboard = lazy(() => import('../horses/EkynAirLiveDashboard'));
const CompareSessionsPage  = lazy(() => import('../sessions/CompareSessionsPage'));
const SessionListPage      = lazy(() => import('../sessions/SessionListPage'));

function Loading({ darkMode }) {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', padding: '60px', color: darkMode ? '#E8EDF2' : '#4A5568' }}>
      {t('common.loading')}
    </div>
  );
}

/**
 * Recharge le cheval :id via le chargeur existant (openHorsePage → fetch
 * /api/horses/:id si absent du state local) quand l'objet n'est pas déjà posé.
 * Reconstruit aussi le rôle de partage depuis sharedHorses (déposé par
 * loadHorses) pour que getShareRole(:id) reste cohérent en deep-link sur un
 * cheval partagé. Owner sur son propre cheval → aucun rôle (null), comme avant.
 */
function useReloadHorse(s, id, obj, setter, page) {
  useEffect(() => {
    if (!obj || obj._id !== id) s.openHorsePage(id, page, setter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    if (s.getShareRole(id)) return;
    const shared = (s.sharedHorses || []).find(h => h._id === id);
    if (shared?.shareRole) s.setShareRoleForHorse(id, shared.shareRole, shared.acceptedAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, s.sharedHorses]);
}

// Cheval pour rendu immédiat : objet déjà posé (state) ou trouvé dans une liste
// déjà chargée (horses/sharedHorses) → pas de flash quand on vient de l'app ;
// écran de chargement seulement en deep-link à froid (le fetch openHorsePage
// remplira ensuite le state).
function pickHorse(s, id, obj) {
  if (obj && obj._id === id) return obj;
  return (s.horses || []).find(h => h._id === id)
      || (s.sharedHorses || []).find(h => h._id === id)
      || null;
}

export function HorseDetailRoute({ s }) {
  const { id } = useParams();
  const navigate = useNavigate();
  useReloadHorse(s, id, s.detailHorse, s.setDetailHorse, PAGES.DETAIL);
  const horse = pickHorse(s, id, s.detailHorse);
  if (!horse) return <Loading darkMode={s.darkMode} />;
  return (
    <HorseDetail horse={horse} darkMode={s.darkMode} currentUser={s.currentUser}
      share={s.getShareRole(id)}
      onBack={() => navigate('/horses')}
      onEdit={(horseOrId) => s.handleEditHorse(horseOrId)}
      onViewSessions={(hid) => {
        const entry = s.getShareRole(hid);
        s.setShareRoleForHorse(hid, entry?.role || null, entry?.acceptedAt || null);
        navigate('/sessions?horse=' + hid);
      }}
    />
  );
}

export function HorseHealthRoute({ s }) {
  const { id } = useParams();
  const navigate = useNavigate();
  useReloadHorse(s, id, s.healthHorse, s.setHealthHorse, PAGES.HEALTH);
  const horse = pickHorse(s, id, s.healthHorse);
  if (!horse) return <Loading darkMode={s.darkMode} />;
  return (
    <HealthJournal horseId={horse._id} horseName={horse.horseName} darkMode={s.darkMode}
      shareRole={s.getShareRole(id)?.role || null} currentUser={s.currentUser}
      onBack={() => navigate('/horses')} />
  );
}

export function HorseLiveRoute({ s, backBtn }) {
  const { id } = useParams();
  const navigate = useNavigate();
  useReloadHorse(s, id, s.liveHorse, s.setLiveHorse, PAGES.LIVE);
  const horse = pickHorse(s, id, s.liveHorse);
  if (!horse) return <Loading darkMode={s.darkMode} />;
  return (
    <div>
      {backBtn(() => navigate('/horses'), 'Mes chevaux')}
      <EkynAirLiveDashboard horseId={horse._id} imei={horse.equipmentId}
        horseName={horse.horseName} horse={horse} darkMode={s.darkMode} />
    </div>
  );
}

export function CompareRoute({ s }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const idsParam = params.get('ids') || '';
  // Mémoïsé sur la chaîne brute : sessionIds garde une identité STABLE entre
  // rendus (sinon le useEffect de CompareSessionsPage [sessionIds] reficherait
  // en boucle). Métadonnées : depuis s.sessions, sinon fallback fetch /api/
  // sessions/:id (interne au composant) — cf. deep-link.
  // Au-delà de MAX_COMPARE_SESSIONS identifiants : les premiers seulement, mention.
  const { ids, truncated } = useMemo(() => parseCompareIds(idsParam), [idsParam]);
  // Plage d'analyse et mode d'axe dans l'URL (align, range, from, to).
  const { range, onRangeChange } = useRangeSearchParams();
  if (ids.length < 2) return <Navigate to="/sessions" replace />;
  return (
    <>
      {truncated && <CompareTruncatedNotice darkMode={s.darkMode} />}
      <CompareSessionsPage sessionIds={ids} sessions={s.sessions} darkMode={s.darkMode}
        onBack={() => navigate('/sessions')} variant="owner" range={range} onRangeChange={onRangeChange} />
    </>
  );
}

export function SessionListRoute({ s, onNavigate }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const horseId = params.get('horse');
  // ?horse=:id → liste filtrée (setSelectedHorse + loadSessions(horseId)). Plain
  // /sessions à l'arrivée (Retour, historique) : le filtre courant est conservé
  // (D232) ; le menu, lui, le remet à zéro (handleNavigate(LIST)). Si ?horse=
  // disparaît de l'URL pendant que la liste est affichée : liste complète.
  // Recharge seulement sur changement effectif (pas de double-load).
  const prevHorseIdRef = useRef(horseId);
  useEffect(() => {
    const hadHorseParam = Boolean(prevHorseIdRef.current);
    prevHorseIdRef.current = horseId;
    if (horseId) {
      if (s.selectedHorse !== horseId) { s.setSelectedHorse(horseId); s.loadSessions(horseId); }
    } else if (hadHorseParam && s.selectedHorse) {
      s.setSelectedHorse(null); s.loadSessions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horseId]);
  // Rechargement à chaque arrivée sur la liste, quel que soit le chemin (D232).
  useSessionsListVisit(s, true, horseId || s.selectedHorse);
  return (
    <SessionListPage sessions={s.sessions} horses={s.horses}
      selectedHorse={s.selectedHorse} setSelectedHorse={s.setSelectedHorse}
      loadSessions={s.loadSessions} onOpenSession={(id) => navigate('/sessions/' + id)}
      onDeleteSession={s.handleDeleteSession} onDeleteSessions={s.handleDeleteSessions}
      darkMode={s.darkMode} currentUser={s.currentUser} onNavigate={onNavigate}
      getShareRole={s.getShareRole}
      onCompare={(cids) => navigate('/compare?ids=' + cids.join(','))} />
  );
}
